const FIELD_LABELS = {
  accountType: "Account type",
  email: "Email address",
  contactEmail: "Contact email",
  password: "Password",
  firstName: "First name",
  familyName: "Family name",
  phone: "Phone number",
  dob: "Date of birth",
  profilePhoto: "Profile photo",
  nationality: "Nationality",
  nationalId: "National ID / serial number",
  idPhoto: "ID photo",
  languages: "Languages",
  universityName: "University name",
  universityMajor: "University major",
  universityYear: "University year",
  governorate: "Governorate",
  address: "Place of residence",
  jobStatus: "Job status",
  gender: "Gender",
  driverLicense: "Driver license",
  ownCar: "Car ownership",
  heardAboutUs: "How you heard about us",
  experience: "Work experience",
  companyName: "Company name",
  contactName: "Contact name",
  contactPhone: "Phone number",
  neededService: "Needed service",
  job_type: "Job type",
};

const COMMON_FIELD_MAP = {
  first_name: "firstName",
  family_name: "familyName",
  profile_photo: "profilePhoto",
  national_id: "nationalId",
  id_photo: "idPhoto",
  university_name: "universityName",
  university_major: "universityMajor",
  university_year: "universityYear",
  job_status: "jobStatus",
  driver_license: "driverLicense",
  own_car: "ownCar",
  heard_about_us: "heardAboutUs",
  company_name: "companyName",
  contact_name: "contactName",
  contact_phone: "contactPhone",
  contact_email: "contactEmail",
  needed_service: "neededService",
};

const GLOBAL_KEYS = new Set(["detail", "error", "reason", "message", "non_field_errors"]);

const asSentence = (value) => {
  const text = String(value || "").trim();
  if (!text) return "";
  return /[.!?]$/.test(text) ? text : `${text}.`;
};

const friendlyCopy = (message, label) => {
  const text = String(message || "").trim();
  const lower = text.toLowerCase();

  if (!text) return `${label} is not valid.`;
  if (lower === "this field is required.") return `${label} is required.`;
  if (lower.includes("already exists") || lower.includes("already in use")) {
    return label.toLowerCase().includes("email")
      ? "An account already uses this email address. Try signing in or use a different email."
      : `${label} is already in use. Please use a different one.`;
  }
  if (lower.includes("valid email")) return "Enter a complete email address, for example name@example.com.";
  if (lower.includes("valid jordanian phone")) return "Enter a Jordanian mobile number such as 0791234567.";
  if (lower.includes("at least") && label === "Password") return "Use a password with at least 8 characters.";
  if (lower.includes("university emails are not allowed")) return "Use a personal email address instead of a university email.";
  if (/^http \d+$/i.test(text)) return "The server could not complete the registration. Please try again.";
  return asSentence(text);
};

const mapBackendField = (field, role) => {
  if (field === "email") return role === "company" ? "contactEmail" : "email";
  return COMMON_FIELD_MAP[field] || field;
};

const collectMessages = (value, field, output) => {
  if (Array.isArray(value)) {
    value.forEach((item) => collectMessages(item, field, output));
    return;
  }

  if (value && typeof value === "object") {
    Object.entries(value).forEach(([nestedKey, nestedValue]) => {
      const nestedField = /^\d+$/.test(nestedKey) ? field : nestedKey;
      collectMessages(nestedValue, nestedField, output);
    });
    return;
  }

  if (value !== undefined && value !== null) output.push({ field, message: String(value) });
};

export const registrationNetworkFailure = () => ({
  status: 0,
  data: null,
  message: "We couldn't reach the registration service. Check your connection and try again.",
});

export const readRegistrationFailure = async (response) => {
  let data = null;

  try {
    const raw = await response.text();
    data = raw ? JSON.parse(raw) : null;
  } catch {
    data = null;
  }

  return {
    status: response.status,
    data,
    message:
      response.status >= 500
        ? "The registration service is temporarily unavailable. Please try again in a moment."
        : "Some registration details need your attention.",
  };
};

export const normalizeRegistrationError = (error, role) => {
  const status = Number(error?.status || 0);
  const payload = error?.data;
  const collected = [];

  if (payload && typeof payload === "object") {
    // Guard metadata is for transport/retry handling, not registration fields.
    if (typeof payload.code === "string" && /^(image_|email_)/.test(payload.code)) {
      collectMessages(payload.detail || error.message, "detail", collected);
    } else {
      Object.entries(payload).forEach(([field, value]) => collectMessages(value, field, collected));
    }
  } else if (typeof payload === "string") {
    collectMessages(payload, null, collected);
  } else if (typeof error === "string") {
    collectMessages(error, null, collected);
  } else if (error?.message) {
    collectMessages(error.message, null, collected);
  }

  const fieldMap = new Map();
  const generalMessages = [];

  collected.forEach(({ field, message }) => {
    if (!field || GLOBAL_KEYS.has(field)) {
      generalMessages.push(friendlyCopy(message, "Registration"));
      return;
    }

    const mappedField = mapBackendField(field, role);
    const label = FIELD_LABELS[mappedField] || String(field).replace(/_/g, " ");
    const messages = fieldMap.get(mappedField) || [];
    messages.push(friendlyCopy(message, label));
    fieldMap.set(mappedField, messages);
  });

  const fieldErrors = Array.from(fieldMap.entries()).map(([name, errors]) => ({
    name,
    errors: [...new Set(errors)],
  }));
  const fieldMessages = fieldErrors.flatMap((item) => item.errors);

  let title = "Please review your registration details";
  let description = error?.message || "We couldn't create your account yet.";

  if (status === 0) {
    title = "We couldn't connect to the server";
    description = "Check your internet connection and make sure the service is available, then try again.";
  } else if (status >= 500) {
    title = "The registration service is temporarily unavailable";
    description = "Your information was not submitted. Please wait a moment and try again.";
  } else if (status === 429) {
    title = "Please wait before checking more images";
    description = generalMessages[0] || "Please wait for the upload cooldown to finish, then try again.";
  } else if (fieldMessages.some((item) => item.toLowerCase().includes("already uses this email"))) {
    title = "This email is already registered";
    description = "Use a different email address or go to the login page if this account belongs to you.";
  } else if (!fieldErrors.length && generalMessages.length) {
    description = generalMessages[0];
  }

  return {
    title,
    description,
    fieldErrors,
    details: [...new Set([...generalMessages, ...fieldMessages])],
  };
};

